import React, { useState, useEffect } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import * as HeroIcons from "@heroicons/react/24/outline";
import {
  LayoutDashboard,
  ChevronDown,
  Menu,
  X,
  LogOut,
  FolderOpen,
  FileText
} from "lucide-react";
import { useSesion } from "../../context/System/SesionContext";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const SidebarItem = ({
  item,
  activeMenu,
  onToggle,
  children,
  isCollapsed,
  location,
  activeLeafId,
  onLeafClick,
  depth = 0
}) => {
  const hasChildren = children && children.length > 0;
  const isActiveParent = activeMenu.menu === item.id && activeMenu.active;

  const isIconBuffer =
    item.icon?.type === "Buffer" && Array.isArray(item.icon.data);
  const base64Icon = isIconBuffer
    ? `data:image/png;base64,${btoa(
        String.fromCharCode(...item.icon.data)
      )}`
    : null;

  const IconComponent = !isIconBuffer && HeroIcons[item.icon];
  const displayLabel = item.id === "MEN046" ? "Imp.Calibracion" : item.menuName;

  const renderIcon = (sizeClass = "h-[18px] w-[18px] min-w-[18px]") => {
    if (base64Icon) {
      return (
        <img
          src={base64Icon}
          alt="icon"
          className={cn(sizeClass, "object-contain brightness-0 invert")}
        />
      );
    }
    if (IconComponent) {
      return <IconComponent className={sizeClass} />;
    }
    if (hasChildren) {
      return <FolderOpen className={sizeClass} />;
    }
    return <FileText className={sizeClass} />;
  };

  if (hasChildren) {
    return (
      <div className="flex flex-col">
        <button
          key={`btn-${item.id}`}
          onClick={() => onToggle(item.id)}
          title={isCollapsed ? displayLabel : undefined}
          className={cn(
            "group flex items-center text-[0.88rem] font-medium rounded-[6px] transition-all duration-200 w-full text-left",
            isCollapsed ? "justify-center px-0 py-2.5" : "justify-between px-3 py-[10px]",
            isActiveParent
              ? "bg-[#004a6b]/50 text-white font-semibold"
              : "text-cyan-100/85 hover:bg-[#004a6b]/70 hover:text-white"
          )}
        >
          <div className="flex items-center min-w-0">
            <span className={cn("shrink-0", !isCollapsed && "mr-[12px]")}>
              {renderIcon("h-[18px] w-[18px] min-w-[18px]")}
            </span>
            {!isCollapsed && <span className="truncate">{displayLabel}</span>}
          </div>
          {!isCollapsed && (
            <ChevronDown
              className={cn(
                "w-[12px] h-[12px] shrink-0 transition-transform duration-200",
                isActiveParent && "rotate-180 text-amber-300"
              )}
            />
          )}
        </button>

        {isActiveParent && !isCollapsed && (
          <div className="flex flex-col relative mt-1 ml-4 pl-1 border-l border-[#004a6b]">
            {children
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((child) => (
                <SidebarItem
                  key={child.id}
                  item={child}
                  activeMenu={activeMenu}
                  onToggle={onToggle}
                  children={child.children}
                  isCollapsed={isCollapsed}
                  location={location}
                  activeLeafId={activeLeafId}
                  onLeafClick={onLeafClick}
                  depth={depth + 1}
                />
              ))}
          </div>
        )}
      </div>
    );
  }

  // Calculate destination path for leaf
  let destination = "#";
  if (item.id === "MEN046") destination = "/MEN046";
  else if (item.id === "MEN045") destination = "/MEN045";
  else if (item.id === "MEN010") destination = "/MEN010";
  else if (item.id === "MEN029") destination = "/MEN029";
  else if (item.path && item.path.trim() !== "") {
    destination = item.path.startsWith("/") ? item.path : `/${item.path}`;
  }

  if (destination === "#") {
    return (
      <div
        key={`static-${item.id}`}
        className={cn(
          "group flex items-center rounded-[6px] text-cyan-100/50 cursor-default",
          isCollapsed
            ? "justify-center px-0 py-2"
            : depth > 0
            ? "pr-3 pl-[12px] py-[6px] text-[0.85rem] mt-1"
            : "px-3 py-[10px] text-[0.88rem]"
        )}
        title={isCollapsed ? displayLabel : ""}
      >
        <span className={cn("shrink-0", !isCollapsed && "mr-[12px]")}>
          {renderIcon(depth > 0 ? "h-[14px] w-[14px] min-w-[14px]" : "h-[18px] w-[18px] min-w-[18px]")}
        </span>
        {!isCollapsed && <span className="truncate">{displayLabel}</span>}
      </div>
    );
  }

  const isActive = activeLeafId === item.id;

  return (
    <Link
      key={`link-${item.id}`}
      to={{ pathname: destination, state: { activeId: item.id } }}
      onClick={() => onLeafClick && onLeafClick(item.id)}
      title={isCollapsed ? displayLabel : ""}
      className={cn(
        "group flex items-center rounded-[6px] transition-all duration-200",
        isCollapsed
          ? "justify-center px-0 py-2.5"
          : depth > 0
          ? "pr-3 pl-[12px] py-[6px] text-[0.85rem] mt-1"
          : "px-3 py-[10px] text-[0.88rem] font-medium",
        isActive
          ? depth > 0
            ? "bg-[#004a6b] text-amber-300 font-bold shadow-sm"
            : "bg-[#004a6b] text-white font-bold border-l-4 border-amber-300 shadow-sm"
          : "text-cyan-100/80 hover:text-white hover:bg-[#004a6b]/60"
      )}
    >
      <span className={cn("shrink-0", !isCollapsed && "mr-[12px]")}>
        {renderIcon(depth > 0 ? "h-[14px] w-[14px] min-w-[14px]" : "h-[18px] w-[18px] min-w-[18px]")}
      </span>
      {!isCollapsed && <span className="truncate">{displayLabel}</span>}
    </Link>
  );
};

const HcorzSidebar = ({ mobileOpen, onMobileClose }) => {
  const { usuarioData, handleClickCerrarSesion } = useSesion();
  const [activeMenu, setActiveMenu] = useState({ menu: null, active: false });
  const [activeLeafId, setActiveLeafId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("atecma_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });
  const location = useLocation();

  const handleToggleCollapsed = (val) => {
    setIsCollapsed(val);
    try {
      localStorage.setItem("atecma_sidebar_collapsed", String(val));
    } catch {}
  };

  const handleToggle = (id) => {
    if (isCollapsed) {
      handleToggleCollapsed(false);
    }
    setActiveMenu((prev) => ({
      menu: prev.menu === id ? null : id,
      active: prev.menu !== id
    }));
  };

  const normalizePath = (node) => {
    let dest = "#";
    if (node.id === "MEN046") dest = "/MEN046";
    else if (node.id === "MEN045") dest = "/MEN045";
    else if (node.id === "MEN010") dest = "/MEN010";
    else if (node.id === "MEN029") dest = "/MEN029";
    else if (node.path && node.path.trim() !== "") {
      dest = node.path.startsWith("/") ? node.path : `/${node.path}`;
    }
    return dest;
  };

  const findMatch = (nodes, currentPath) => {
    for (const node of nodes) {
      const dest = normalizePath(node);
      if (dest === currentPath) return node.id;
      if (node.children && node.children.length > 0) {
        const childMatch = findMatch(node.children, currentPath);
        if (childMatch) return childMatch;
      }
    }
    return null;
  };

  const findNode = (nodes, targetId) => {
    for (const node of nodes) {
      if (node.id === targetId) return node;
      if (node.children) {
        const found = findNode(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Clone the tree so we can inject the new modules before matching
  const tree = React.useMemo(() => {
    const cloned = JSON.parse(JSON.stringify(usuarioData?.menus || []));

    const sistemasMenu = cloned.find(
      (m) => m.menuName && m.menuName.toLowerCase() === "sistemas"
    );
    if (sistemasMenu) {
      if (!sistemasMenu.children) sistemasMenu.children = [];
      if (!sistemasMenu.children.some((c) => c.id === "MEN_API_CONFIG")) {
        sistemasMenu.children.push({
          id: "MEN_API_CONFIG",
          menuName: "Config. API IA",
          path: "/config-api",
          icon: "SparklesIcon",
          order: 999
        });
      }
    }

    const manttoMenu = cloned.find(
      (m) => m.menuName && m.menuName.toLowerCase().includes("mantto")
    );
    if (manttoMenu) {
      if (!manttoMenu.children) manttoMenu.children = [];
      if (!manttoMenu.children.some((c) => c.id === "MEN_REGI_CALIB")) {
        manttoMenu.children.push({
          id: "MEN_REGI_CALIB",
          menuName: "Regi.Calibracion",
          path: "/regi-calibracion",
          icon: "DocumentPlusIcon",
          order: 998
        });
      }
      if (!manttoMenu.children.some((c) => c.id === "MEN_INV_EQUIPOS")) {
        manttoMenu.children.push({
          id: "MEN_INV_EQUIPOS",
          menuName: "Inv. Equipos",
          path: "/inventario-equipos",
          icon: "ArchiveBoxIcon",
          order: 997
        });
      }
      if (!manttoMenu.children.some((c) => c.id === "MEN_PROG_CALIB")) {
        manttoMenu.children.push({
          id: "MEN_PROG_CALIB",
          menuName: "Prog. Calibraciones",
          path: "/prog-calibracion",
          icon: "CalendarIcon",
          order: 996
        });
      }
    }
    return cloned;
  }, [usuarioData?.menus]);

  const handleLeafClick = (id) => {
    sessionStorage.setItem("sidebarActiveId", id);
    setActiveLeafId(id);
    if (mobileOpen && onMobileClose) onMobileClose();
  };

  useEffect(() => {
    const currentPath = location.pathname;

    const activeParent = tree.find((parent) =>
      parent.children?.some((child) => normalizePath(child) === currentPath)
    );
    if (activeParent) {
      setActiveMenu({ menu: activeParent.id || activeParent.menuid, active: true });
    }

    const storedId = sessionStorage.getItem("sidebarActiveId");
    let targetId = null;

    if (storedId) {
      const node = findNode(tree, storedId);
      if (node && normalizePath(node) === currentPath) {
        targetId = storedId;
      }
    }

    if (!targetId) {
      targetId = findMatch(tree, currentPath);
    }

    if (location.state?.activeId) {
      targetId = location.state.activeId;
      sessionStorage.setItem("sidebarActiveId", targetId);
    }

    setActiveLeafId(targetId);
  }, [location.pathname, location.state, tree]);

  const isMini = isCollapsed && !mobileOpen;

  return (
    <>
      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/80 backdrop-blur-sm md:hidden transition-opacity"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar Container - SAP Belize Ocean Blue (#005f88) */}
      <aside
        className={cn(
          "bg-[#005f88] text-white h-screen flex flex-col border-r border-[#004a6b] shadow-xl transition-all duration-300 ease-in-out fixed md:relative inset-y-0 left-0 z-50 shrink-0",
          isMini ? "w-[68px]" : "w-[260px]",
          mobileOpen ? "translate-x-0 w-[260px]" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Sidebar Header - Strict height h-[56px] matching top header */}
        <div
          className={cn(
            "h-[56px] flex items-center shrink-0 border-b border-[#004a6b] bg-[#004a6b]/60 px-3 transition-all",
            isMini ? "justify-center" : "justify-between"
          )}
        >
          {isMini ? (
            <button
              onClick={() => handleToggleCollapsed(false)}
              className="p-1.5 rounded hover:bg-[#003852] text-cyan-200 hover:text-white transition-colors cursor-pointer"
              title="Expandir menú"
            >
              <Menu className="w-5 h-5" />
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="bg-amber-400 text-slate-900 p-1.5 rounded flex items-center justify-center shrink-0 font-black shadow">
                  <span className="font-bold text-slate-900 text-xs leading-none">AT</span>
                </div>
                <span className="font-bold text-white tracking-wide text-[1.15rem] whitespace-nowrap">
                  ATECMA <span className="text-amber-300">SIS</span>
                </span>
              </div>

              {/* Desktop Collapse Hamburger */}
              <button
                onClick={() => handleToggleCollapsed(true)}
                className="hidden md:flex p-1.5 rounded hover:bg-[#003852] text-cyan-200 hover:text-white transition-colors cursor-pointer"
                title="Colapsar menú (solo iconos)"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Mobile Close Button */}
              <button
                onClick={onMobileClose}
                className="md:hidden text-cyan-200 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1 overflow-x-hidden">
          <NavLink
            to="/dashboard"
            onClick={() => {
              if (mobileOpen && onMobileClose) onMobileClose();
            }}
            title={isMini ? "Dashboard" : undefined}
            className={({ isActive }) =>
              cn(
                "group flex items-center text-[0.88rem] font-medium rounded-[6px] transition-all duration-200",
                isMini ? "justify-center px-0 py-2.5" : "px-3 py-[10px]",
                isActive
                  ? "bg-[#004a6b] text-white font-bold border-l-4 border-amber-300 shadow-sm"
                  : "text-cyan-100/85 hover:bg-[#004a6b]/70 hover:text-white"
              )
            }
          >
            <LayoutDashboard
              className={cn(
                "h-[18px] w-[18px] min-w-[18px] text-center",
                !isMini && "mr-[12px]"
              )}
            />
            {!isMini && <span>Dashboard</span>}
          </NavLink>

          <div className="space-y-1">
            {tree
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((parent) => (
                <SidebarItem
                  key={parent.id}
                  item={parent}
                  activeMenu={activeMenu}
                  onToggle={handleToggle}
                  children={parent.children}
                  isCollapsed={isMini}
                  location={location}
                  activeLeafId={activeLeafId}
                  onLeafClick={handleLeafClick}
                  depth={0}
                />
              ))}
          </div>

          <div className="pt-2 mt-2 border-t border-white/15 opacity-80" />

          {/* Bottom Logout Button */}
          <div className="space-y-1">
            <button
              onClick={handleClickCerrarSesion}
              title={isMini ? "Cerrar Sesión" : undefined}
              className={cn(
                "group flex items-center text-[0.88rem] rounded-[6px] text-red-300 hover:bg-red-400/20 transition-colors w-full text-left mt-1",
                isMini ? "justify-center px-0 py-2.5" : "px-3 py-[10px]"
              )}
            >
              <LogOut
                className={cn(
                  "h-[18px] w-[18px] min-w-[18px] text-center",
                  !isMini && "mr-[12px]"
                )}
              />
              {!isMini && <span>Cerrar Sesión</span>}
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default HcorzSidebar;
